import React, {useState, useEffect, useCallback, useRef} from 'react'
import axios from 'axios'
import {getFacebookProfiles} from './core/_requests'
import {IsraelCitySelector} from './IsraelCitySelector'
import {buildMarketplaceTitle} from '../../utils/marketplaceTitle'

const API_URL = process.env.REACT_APP_API_URL || ''

const CITY_COORDINATES: Record<string, {lat: number; lng: number}> = {
  'תל אביב': {lat: 32.0853, lng: 34.7818},
  'ירושלים': {lat: 31.7683, lng: 35.2137},
  'חיפה': {lat: 32.7940, lng: 34.9896},
  'באר שבע': {lat: 31.2530, lng: 34.7915},
  'ראשון לציון': {lat: 31.9730, lng: 34.7925},
  'פתח תקווה': {lat: 32.0841, lng: 34.8878},
  'אשדוד': {lat: 31.8040, lng: 34.6553},
  'נתניה': {lat: 32.3215, lng: 34.8532},
  'חולון': {lat: 32.0158, lng: 34.7794},
  'בני ברק': {lat: 32.0834, lng: 34.8331},
  'רמת גן': {lat: 32.0680, lng: 34.8241},
  'אשקלון': {lat: 31.6688, lng: 34.5743},
  'רחובות': {lat: 31.8928, lng: 34.8113},
  'בת ים': {lat: 32.0231, lng: 34.7503},
  'הרצליה': {lat: 32.1663, lng: 34.8463},
  'כפר סבא': {lat: 32.1750, lng: 34.9066},
  'רעננה': {lat: 32.1836, lng: 34.8706},
  'מודיעין': {lat: 31.8977, lng: 35.0104},
  'עפולה': {lat: 32.6100, lng: 35.2919},
  'טבריה': {lat: 32.7922, lng: 35.5312},
  'נצרת': {lat: 32.6996, lng: 35.3035},
  'עכו': {lat: 32.9261, lng: 35.0764},
  'קריית שמונה': {lat: 33.2072, lng: 35.5706},
}

const NEIGHBORHOOD_TO_CITY: Record<string, string> = {
  'יעקב אבינו': 'באר שבע', 'נווה זאב': 'באר שבע', 'רמות': 'באר שבע',
  'פלורנטין': 'תל אביב', 'נווה צדק': 'תל אביב', 'יפו': 'תל אביב', 'רמת אביב': 'תל אביב',
  'הדר': 'חיפה', 'כרמל': 'חיפה',
  'תלפיות': 'ירושלים', 'בית הכרם': 'ירושלים', 'קטמון': 'ירושלים',
}

interface PostData {
  _id: string
  name: string
  post_content: string
  media_url?: string
  media?: string[]
  price?: number
}

interface AnalyzedPost {
  listing_type: string
  title: string
  price: string
  description: string
  category_id: string
  condition: string
  city: string
  latitude: number
  longitude: number
  photos: string[]
  bedrooms: string
  bathrooms: string
  square_meters: string
  property_type: string
  auto_reply_message: string
  brand: string
  color: string
  size: string
  profile_id: string
  // Profiles this listing publishes from — one MarketplaceListing row per profile.
  // Absent until the user touches the picker, at which point it overrides the
  // page-level defaults (all online parent profiles).
  profile_ids?: string[]
  // Vehicle fields the analyzer may return; used to derive a title.
  vehicle_make?: string
  vehicle_model?: string
  vehicle_year?: string
}

interface BulkMarketplaceModalProps {
  show: boolean
  onClose: () => void
  onSuccess: (updatedPostIds: string[]) => void
  posts: PostData[]
  campaignId: string
  campaignName: string
}

function getAuthToken() {
  try {
    const auth = localStorage.getItem('kt-auth-react-v')
    if (auth) return JSON.parse(auth).api_token
  } catch {}
  return null
}

function detectCity(content: string): string {
  for (const [neighborhood, city] of Object.entries(NEIGHBORHOOD_TO_CITY)) {
    if (content.includes(neighborhood)) return city
  }
  for (const city of Object.keys(CITY_COORDINATES)) {
    if (content.includes(city)) return city
  }
  return ''
}

// Remove the boilerplate contact block ("📞 לפרטים: 💬 : ") from ad content.
function stripContactBlock(text: string): string {
  return (text || '')
    .replace(/📞\s*לפרטים\s*:?/g, '')
    .replace(/💬\s*:?/g, '')
    .replace(/[^\S\n]+/g, ' ')      // collapse spaces/tabs, keep newlines
    .replace(/\n{3,}/g, '\n\n')     // collapse excess blank lines
    .trim()
}

function mapAIResponse(data: any, post: PostData, campaignName: string): AnalyzedPost {
  let listingType = data.listing_type || 'home_sale'
  if (listingType === 'rental') listingType = 'home_rent'

  // City is derived ONLY from the campaign name (not the post content).
  const detectedCity = detectCity(campaignName || '')
  const bedrooms = data.bedrooms || 0

  const generateTitle = () => {
    switch (listingType) {
      case 'home_rent': return bedrooms > 0 ? `דירת ${bedrooms} חדרים להשכרה` : 'דירה להשכרה'
      case 'home_sale': return bedrooms > 0 ? `דירת ${bedrooms} חדרים למכירה` : 'דירה למכירה'
      case 'vehicle': return data.vehicle_year ? `רכב שנת ${data.vehicle_year} למכירה` : 'רכב למכירה'
      default: return data.title || post.name || 'פריט למכירה'
    }
  }

  const mediaUrls = post.media?.length ? post.media :
    post.media_url ? post.media_url.split(',').map((u: string) => u.trim()).filter(Boolean) : []

  return {
    listing_type: listingType,
    title: data.title || generateTitle(),
    // 1, not 0: Facebook won't publish a 0-price listing, and showing the floor the
    // server would apply anyway means the price box says what will actually be sent.
    price: (data.price || data.suggested_price || 1).toString(),
    description: stripContactBlock(data.description || ''),
    category_id: data.suggested_category_id || '',
    condition: data.item_condition || 'used_good',
    city: detectedCity || data.city || '',
    latitude: (detectedCity ? CITY_COORDINATES[detectedCity]?.lat : 0) || data.latitude || 0,
    longitude: (detectedCity ? CITY_COORDINATES[detectedCity]?.lng : 0) || data.longitude || 0,
    photos: mediaUrls,
    bedrooms: (data.bedrooms || '').toString(),
    bathrooms: (data.bathrooms || '').toString(),
    square_meters: (data.square_meters || '').toString(),
    property_type: data.property_type || '',
    auto_reply_message: '',
    brand: '',
    color: '',
    size: '',
    profile_id: '',
  }
}

function getDefaultAnalysis(post: PostData, campaignName: string): AnalyzedPost {
  const mediaUrls = post.media?.length ? post.media :
    post.media_url ? post.media_url.split(',').map((u: string) => u.trim()).filter(Boolean) : []
  return {
    listing_type: 'item',
    title: post.name || '',
    price: (post.price || 1).toString(),
    description: stripContactBlock(post.post_content || ''),
    category_id: '',
    condition: 'used_good',
    city: detectCity(campaignName || ''),
    latitude: 0,
    longitude: 0,
    photos: mediaUrls,
    bedrooms: '',
    bathrooms: '',
    square_meters: '',
    property_type: '',
    auto_reply_message: '',
    brand: '',
    color: '',
    size: '',
    profile_id: '',
  }
}

const LISTING_TYPE_OPTIONS = [
  {value: 'item', label: 'פריט'},
  {value: 'vehicle', label: 'רכב'},
  {value: 'home_rent', label: 'השכרה'},
  {value: 'home_sale', label: 'מכירה'},
]

export const BulkMarketplaceModal: React.FC<BulkMarketplaceModalProps> = ({
  show, onClose, onSuccess, posts, campaignId, campaignName
}) => {
  const [profiles, setProfiles] = useState<any[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState('')
  const [analyses, setAnalyses] = useState<Map<string, AnalyzedPost>>(new Map())
  const [analyzingIds, setAnalyzingIds] = useState<Set<string>>(new Set())
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState({current: 0, total: 0, errors: [] as string[]})
  const [done, setDone] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Profiles selected by default for every listing (all online parent profiles).
  // A listing's own analysis.profile_ids overrides this once the user touches it.
  const [defaultProfileIds, setDefaultProfileIds] = useState<string[]>([])

  // Global location fallback
  const [globalCity, setGlobalCity] = useState('')
  const [globalLat, setGlobalLat] = useState(0)
  const [globalLng, setGlobalLng] = useState(0)

  // Load profiles
  useEffect(() => {
    if (!show) return
    getFacebookProfiles().then(res => {
      const p = res.profiles || []
      setProfiles(p)
      // Default to every parent profile whose bot is currently online (server sets
      // is_online off last_health_check). A profile whose bot isn't running can't
      // publish, so pre-selecting it would just queue listings that sit pending.
      // If nothing is online, fall back to all of them rather than an empty form.
      const online = p.filter((x: any) => x.is_online).map((x: any) => x.id)
      const defaults = online.length ? online : p.map((x: any) => x.id)
      setDefaultProfileIds(defaults)
      if (p.length > 0) setSelectedProfileId(p[0].id)
    }).catch(() => {})
  }, [show])

  // The parent builds the `posts` prop inline (posts.filter(...)), so its identity
  // changes on every parent render. Keying the analyze effect on the array itself
  // restarted a fresh, uncancelled analyzeAll loop each time — dozens of them piled
  // up in parallel and the browser gave up with ERR_INSUFFICIENT_RESOURCES. Key on
  // the post ids instead, and read the posts through a ref so they stay current.
  const postsRef = useRef(posts)
  postsRef.current = posts
  const postIdsKey = posts.map(p => p._id).join(',')

  // Analyze all posts on open
  useEffect(() => {
    const postsToAnalyze = postsRef.current
    if (!show || postsToAnalyze.length === 0) return
    setAnalyses(new Map())
    setExcludedIds(new Set())
    setDone(false)
    setProgress({current: 0, total: 0, errors: []})

    let cancelled = false

    const analyzeAll = async () => {
      const CONCURRENCY = 3
      const allIds = new Set(postsToAnalyze.map(p => p._id))
      setAnalyzingIds(allIds)

      for (let i = 0; i < postsToAnalyze.length; i += CONCURRENCY) {
        if (cancelled) return
        const batch = postsToAnalyze.slice(i, i + CONCURRENCY)
        await Promise.all(batch.map(async (post) => {
          try {
            const response = await axios.post(`${API_URL}/api/marketplace/analyze-post`, {
              post_content: post.post_content || post.name
            })
            if (cancelled) return
            if (response.data) {
              setAnalyses(prev => new Map(prev).set(post._id, mapAIResponse(response.data, post, campaignName)))
            } else {
              setAnalyses(prev => new Map(prev).set(post._id, getDefaultAnalysis(post, campaignName)))
            }
          } catch {
            if (cancelled) return
            setAnalyses(prev => new Map(prev).set(post._id, getDefaultAnalysis(post, campaignName)))
          } finally {
            if (!cancelled) {
              setAnalyzingIds(prev => {
                const next = new Set(prev)
                next.delete(post._id)
                return next
              })
            }
          }
        }))
      }
    }

    analyzeAll()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, postIdsKey, campaignName])

  // value is string | string[] — profile_ids holds the profiles a listing publishes from.
  const updateField = useCallback((postId: string, field: string, value: string | string[]) => {
    setAnalyses(prev => {
      const next = new Map(prev)
      const current = next.get(postId)
      if (current) {
        next.set(postId, {...current, [field]: value})
      }
      return next
    })
    // Clear validation errors for this post when editing
    setValidationErrors(prev => {
      if (!prev.has(postId)) return prev
      const next = new Map(prev)
      next.delete(postId)
      return next
    })
  }, [])

  const activePosts = posts.filter(p => !excludedIds.has(p._id))
  const analyzedCount = posts.length - analyzingIds.size
  const isAnalyzing = analyzingIds.size > 0

  const [validationErrors, setValidationErrors] = useState<Map<string, string[]>>(new Map())

  const validateAll = (): boolean => {
    const errors = new Map<string, string[]>()
    let valid = true

    for (const post of activePosts) {
      const a = analyses.get(post._id)
      if (!a) continue
      const postErrors: string[] = []

      if (!((a.profile_ids || defaultProfileIds).length)) postErrors.push('חסר פרופיל')
      // No title check — it isn't typed any more, and buildMarketplaceTitle always
      // produces one at submit, so this could only ever block on our own derivation.
      if (!a.city && !globalCity) postErrors.push('חסרה עיר')

      if (postErrors.length > 0) {
        errors.set(post._id, postErrors)
        valid = false
      }
    }

    setValidationErrors(errors)
    return valid
  }

  const handleSubmit = async () => {
    if (activePosts.length === 0) return
    if (!validateAll()) return

    setSubmitting(true)
    const total = activePosts.length
    setProgress({current: 0, total, errors: []})

    const successIds: string[] = []
    const token = getAuthToken()

    for (let i = 0; i < activePosts.length; i++) {
      const post = activePosts[i]
      const analysis = analyses.get(post._id)
      if (!analysis) continue

      const city = analysis.city || globalCity
      const lat = analysis.latitude || globalLat || (city ? CITY_COORDINATES[city]?.lat : 0) || 0
      const lng = analysis.longitude || globalLng || (city ? CITY_COORDINATES[city]?.lng : 0) || 0

      try {
        const res = await fetch(`${API_URL}/api/marketplace/synced-listings/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            // The server creates one listing per profile and dispatches each on its
            // own channel; profile_id is kept only so an older server still works.
            profile_ids: analysis.profile_ids || defaultProfileIds,
            profile_id: (analysis.profile_ids || defaultProfileIds)[0],
            listing_type: analysis.listing_type === 'home_rent' ? 'rental' : analysis.listing_type,
            title: String(analysis.title || '').trim() || buildMarketplaceTitle({
              listing_type: analysis.listing_type,
              bedrooms: analysis.bedrooms,
              vehicle_make: analysis.vehicle_make,
              vehicle_model: analysis.vehicle_model,
              vehicle_year: analysis.vehicle_year,
            }, post),
            description: analysis.description,
            price: parseFloat(analysis.price) > 0 ? parseFloat(analysis.price) : 1,
            condition: analysis.listing_type === 'item' ? (analysis.condition || 'used_good') : undefined,
            category_id: analysis.category_id || '1670493229902393',
            latitude: lat,
            longitude: lng,
            city: city,
            photo_urls: analysis.photos,
            source_post_id: post._id,
            source_campaign_id: campaignId,
            auto_reply_message: analysis.auto_reply_message || undefined,
            item_brand: analysis.brand || undefined,
            item_color: analysis.color || undefined,
            item_size: analysis.size || undefined,
            property_data: (analysis.listing_type === 'home_rent' || analysis.listing_type === 'home_sale') ? {
              bedrooms: parseInt(analysis.bedrooms) || 0,
              bathrooms: parseInt(analysis.bathrooms) || 0,
              square_meters: parseInt(analysis.square_meters) || 0,
              property_type: analysis.property_type || 'apartment',
            } : undefined,
          }),
        })
        // fetch() does NOT throw on HTTP 4xx/5xx — check the response so failed
        // creates don't get marked as success in the UI.
        const data = await res.json().catch(() => null)
        if (!res.ok || (data && data.success === false)) {
          throw new Error((data && (data.error || data.message)) || `HTTP ${res.status}`)
        }
        successIds.push(post._id)
      } catch (err: any) {
        setProgress(prev => ({
          ...prev,
          errors: [...prev.errors, `"${analysis.title}": ${err.message}`],
        }))
      }

      setProgress(prev => ({...prev, current: i + 1}))

      // Delay between requests
      if (i < activePosts.length - 1) {
        await new Promise(r => setTimeout(r, 1000))
      }
    }

    setSubmitting(false)
    setDone(true)

    if (successIds.length > 0) {
      onSuccess(successIds)
    }
  }

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div
        style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998}}
        onClick={submitting ? undefined : onClose}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '95vw',
        maxWidth: '1200px',
        maxHeight: '90vh',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        direction: 'rtl',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e4e6ef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <h3 style={{margin: 0, fontSize: '18px', fontWeight: 700}}>🛒 פרסום מרובה במרקטפלייס</h3>
            <span style={{
              background: '#667eea',
              color: '#fff',
              padding: '2px 10px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 600,
            }}>
              {activePosts.length} מודעות
            </span>
            {campaignName && (
              <span style={{color: '#a1a5b7', fontSize: '13px'}}>
                • {campaignName}
              </span>
            )}
          </div>
          {!submitting && (
            <button onClick={onClose} style={{
              background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#a1a5b7', padding: '4px',
            }}>✕</button>
          )}
        </div>

        {/* Global Controls */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f1f3f5',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
          flexShrink: 0,
          background: '#fafbfc',
        }}>
          {/* Default profiles — applied to every listing that hasn't been given its
              own set. Starts as all online parent profiles. */}
          <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
            <label style={{fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap'}}>פרופילים ברירת מחדל:</label>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
              {profiles.map(p => {
                const on = defaultProfileIds.includes(p.id)
                return (
                  <button
                    key={p.id}
                    type='button'
                    onClick={() => setDefaultProfileIds(prev => (
                      prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                    ))}
                    title={(p as any).is_online ? 'מחובר' : 'לא מחובר כרגע — הפרסום ימתין'}
                    style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      cursor: 'pointer',
                      border: on ? '1px solid #3699ff' : '1px solid #e4e6ef',
                      background: on ? '#3699ff' : '#fff',
                      color: on ? '#fff' : '#7e8299',
                      fontWeight: on ? 600 : 400,
                    }}
                  >
                    {(p as any).is_online ? '🟢' : '⚪'} {p.name}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Default City */}
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <label style={{fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap'}}>עיר ברירת מחדל:</label>
            <IsraelCitySelector
              value={globalCity ? {lat: globalLat, lng: globalLng, city: globalCity} : undefined}
              onChange={(loc: {lat: number; lng: number; city: string}) => {
                setGlobalCity(loc.city)
                setGlobalLat(loc.lat)
                setGlobalLng(loc.lng)
              }}
              placeholder="בחר עיר..."
            />
          </div>

          {/* Analysis Progress */}
          {isAnalyzing && (
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#3699ff', fontSize: '13px'}}>
              <span className="spinner-border spinner-border-sm" />
              מנתח {analyzedCount}/{posts.length} פוסטים...
            </div>
          )}
        </div>

        {/* Posts Grid */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px',
        }}>
          {done ? (
            <div style={{textAlign: 'center', padding: '40px'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>✅</div>
              <h3 style={{fontSize: '20px', fontWeight: 700, marginBottom: '8px'}}>
                {progress.total - progress.errors.length} מודעות נוצרו בהצלחה!
              </h3>
              {progress.errors.length > 0 && (
                <div style={{marginTop: '16px', textAlign: 'right'}}>
                  <p style={{color: '#f64e60', fontWeight: 600}}>{progress.errors.length} שגיאות:</p>
                  {progress.errors.map((err, i) => (
                    <div key={i} style={{fontSize: '12px', color: '#7e8299', marginBottom: '4px'}}>• {err}</div>
                  ))}
                </div>
              )}
              <button onClick={onClose} style={{
                marginTop: '24px',
                padding: '10px 32px',
                background: '#3699ff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}>
                סגור
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
            }}>
              {posts.map(post => {
                if (excludedIds.has(post._id)) return null
                const analysis = analyses.get(post._id)
                const isAnalyzingThis = analyzingIds.has(post._id)
                const thumb = (post.media?.length ? post.media[0] :
                  post.media_url?.split(',')[0]?.trim()) || ''
                const cardErrors = validationErrors.get(post._id)

                return (
                  <div key={post._id} style={{
                    border: cardErrors ? '2px solid #f64e60' : '1px solid #e4e6ef',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    background: cardErrors ? '#fff5f5' : '#fff',
                    position: 'relative',
                    opacity: isAnalyzingThis ? 0.7 : 1,
                    transition: 'opacity 0.2s',
                  }}>
                    {/* Remove button */}
                    <button
                      onClick={() => setExcludedIds(prev => new Set(prev).add(post._id))}
                      style={{
                        position: 'absolute',
                        top: '6px',
                        left: '6px',
                        zIndex: 2,
                        background: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="הסר מהרשימה"
                    >✕</button>

                    {/* Thumbnail */}
                    {thumb ? (
                      <div style={{
                        height: '120px',
                        background: `url(${thumb}) center/cover no-repeat`,
                        borderBottom: '1px solid #f1f3f5',
                      }} />
                    ) : (
                      <div style={{
                        height: '120px',
                        background: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#c4c4c4',
                        fontSize: '32px',
                        borderBottom: '1px solid #f1f3f5',
                      }}>📷</div>
                    )}

                    {/* Analyzing overlay */}
                    {isAnalyzingThis && (
                      <div style={{
                        position: 'absolute',
                        top: '120px',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255,255,255,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}>
                        <div style={{textAlign: 'center'}}>
                          <span className="spinner-border spinner-border-sm" style={{marginBottom: '8px'}} />
                          <div style={{fontSize: '12px', color: '#7e8299'}}>מנתח...</div>
                        </div>
                      </div>
                    )}

                    {/* Fields */}
                    {(() => {
                      const s = {input: {width: '100%', padding: '5px 8px', border: '1px solid #e4e6ef', borderRadius: '4px', fontSize: '12px', marginBottom: '6px'} as React.CSSProperties}
                      const lt = analysis?.listing_type || 'home_sale'
                      return (
                        <div style={{padding: '10px 12px'}}>
                          {/* Profile Selector — one listing can go out from several
                              profiles; each selected profile publishes its own copy. */}
                          {(() => {
                            const chosen: string[] = analysis?.profile_ids || defaultProfileIds
                            const toggle = (id: string) => {
                              const next = chosen.includes(id) ? chosen.filter(x => x !== id) : [...chosen, id]
                              updateField(post._id, 'profile_ids', next)
                            }
                            return (
                              <div style={{background: '#f0f4ff', border: '1px solid #e4e6ef', borderRadius: '4px', padding: '6px 8px', marginBottom: '6px'}}>
                                <div style={{fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: '#3f4254'}}>
                                  פרופילים לפרסום * {chosen.length > 0 && <span style={{color: '#7e8299', fontWeight: 400}}>({chosen.length})</span>}
                                </div>
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                                  {profiles.map(p => {
                                    const on = chosen.includes(p.id)
                                    return (
                                      <button
                                        key={p.id}
                                        type='button'
                                        disabled={isAnalyzingThis}
                                        onClick={() => toggle(p.id)}
                                        title={(p as any).is_online ? 'מחובר' : 'לא מחובר כרגע — הפרסום ימתין'}
                                        style={{
                                          fontSize: '11px',
                                          padding: '3px 8px',
                                          borderRadius: '999px',
                                          cursor: isAnalyzingThis ? 'default' : 'pointer',
                                          border: on ? '1px solid #3699ff' : '1px solid #e4e6ef',
                                          background: on ? '#3699ff' : '#fff',
                                          color: on ? '#fff' : '#7e8299',
                                          fontWeight: on ? 600 : 400,
                                        }}
                                      >
                                        {(p as any).is_online ? '🟢' : '⚪'} {p.name}
                                      </button>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          })()}

                          {/* Listing Type */}
                          <select value={lt} onChange={e => updateField(post._id, 'listing_type', e.target.value)} disabled={isAnalyzingThis} style={{...s.input, background: '#f8f9fa'}}>
                            {LISTING_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>

                          {/* Title */}
                          {/* Read-only: Facebook won't let a listing's title be changed
                              after publishing, so editing one here would promise something
                              we can't deliver on the next sync. Shown so you can see what
                              will go out. */}
                          <div
                            title="לא ניתן לערוך כותרת — פייסבוק לא מאפשרת לשנות כותרת של מודעה"
                            style={{...s.input, fontWeight: 600, fontSize: '13px', background: '#f5f6f8', color: '#3f4254', cursor: 'not-allowed'}}
                          >
                            {analysis?.title || <span style={{color: '#a1a5b7'}}>כותרת תיווצר אוטומטית</span>}
                          </div>

                          {/* Price */}
                          <input type="number" value={analysis?.price ?? '0'} onChange={e => updateField(post._id, 'price', e.target.value)} disabled={isAnalyzingThis} placeholder="מחיר" min="0" style={s.input} />

                          {/* City */}
                          <IsraelCitySelector
                            value={analysis?.city ? {lat: analysis.latitude, lng: analysis.longitude, city: analysis.city} : undefined}
                            onChange={(loc: {lat: number; lng: number; city: string}) => {
                              setAnalyses(prev => {
                                const next = new Map(prev)
                                const cur = next.get(post._id)
                                if (cur) next.set(post._id, {...cur, city: loc.city, latitude: loc.lat, longitude: loc.lng})
                                return next
                              })
                            }}
                            placeholder="בחר עיר..."
                          />
                          <div style={{marginBottom: '6px'}} />

                          {/* Description */}
                          <textarea value={analysis?.description || ''} onChange={e => updateField(post._id, 'description', e.target.value)} disabled={isAnalyzingThis} placeholder="תיאור" rows={2} style={{...s.input, resize: 'vertical'}} />

                          {/* Auto Reply */}
                          <input type="text" value={analysis?.auto_reply_message || ''} onChange={e => updateField(post._id, 'auto_reply_message', e.target.value)} disabled={isAnalyzingThis} placeholder="הודעת תגובה אוטומטית" style={s.input} />

                          {/* Condition - items only */}
                          {lt === 'item' && (
                            <>
                              <select value={analysis?.condition || 'used_good'} onChange={e => updateField(post._id, 'condition', e.target.value)} disabled={isAnalyzingThis} style={{...s.input, background: '#f8f9fa'}}>
                                <option value="new">חדש</option>
                                <option value="used_like_new">כמו חדש</option>
                                <option value="used_good">מצב טוב</option>
                                <option value="used_fair">מצב סביר</option>
                              </select>
                              <div style={{display: 'flex', gap: '6px'}}>
                                <input type="text" value={analysis?.brand || ''} onChange={e => updateField(post._id, 'brand', e.target.value)} placeholder="מותג" style={{...s.input, flex: 1}} />
                                <input type="text" value={analysis?.color || ''} onChange={e => updateField(post._id, 'color', e.target.value)} placeholder="צבע" style={{...s.input, flex: 1}} />
                              </div>
                              <input type="text" value={analysis?.size || ''} onChange={e => updateField(post._id, 'size', e.target.value)} placeholder="מידה" style={s.input} />
                            </>
                          )}

                          {/* Real estate fields */}
                          {(lt === 'home_rent' || lt === 'home_sale') && (
                            <>
                              <div style={{display: 'flex', gap: '6px'}}>
                                <input type="number" value={analysis?.bedrooms || ''} onChange={e => updateField(post._id, 'bedrooms', e.target.value)} placeholder="חדרים" min="0" style={{...s.input, flex: 1}} />
                                <input type="number" value={analysis?.bathrooms || ''} onChange={e => updateField(post._id, 'bathrooms', e.target.value)} placeholder="חדרי רחצה" min="0" style={{...s.input, flex: 1}} />
                              </div>
                              <div style={{display: 'flex', gap: '6px'}}>
                                <input type="number" value={analysis?.square_meters || ''} onChange={e => updateField(post._id, 'square_meters', e.target.value)} placeholder="מ״ר" min="0" style={{...s.input, flex: 1}} />
                                <select value={analysis?.property_type || 'apartment'} onChange={e => updateField(post._id, 'property_type', e.target.value)} style={{...s.input, flex: 1, background: '#f8f9fa'}}>
                                  <option value="apartment">דירה</option>
                                  <option value="house">בית פרטי / קוטג׳</option>
                                  <option value="penthouse">פנטהאוז</option>
                                  <option value="garden_apartment">דירת גן</option>
                                  <option value="duplex">דופלקס</option>
                                  <option value="condo">קונדו</option>
                                  <option value="townhouse">טאון האוס</option>
                                  <option value="studio">סטודיו</option>
                                  <option value="private_room-shared_room">חדר</option>
                                  <option value="land">מגרש</option>
                                  <option value="agricultural_lot">מגרש חקלאי</option>
                                  <option value="multi-family">מרובה משפחות</option>
                                  <option value="office">משרד</option>
                                  <option value="retail">חנות</option>
                                  <option value="warehouse">מחסן</option>
                                  <option value="commercial">מסחרי (כללי)</option>
                                </select>
                              </div>
                            </>
                          )}

                          {/* Photos count */}
                          {analysis && analysis.photos.length > 0 && (
                            <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '2px'}}>
                              📷 {analysis.photos.length} תמונות
                            </div>
                          )}

                          {/* Validation errors */}
                          {cardErrors && (
                            <div style={{marginTop: '6px', padding: '6px 8px', background: '#fff0f0', borderRadius: '4px', border: '1px solid #f64e60'}}>
                              {cardErrors.map((err, i) => (
                                <div key={i} style={{fontSize: '11px', color: '#f64e60', fontWeight: 600}}>⚠ {err}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e4e6ef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            background: '#fafbfc',
          }}>
            {submitting ? (
              <div style={{flex: 1}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                  <span className="spinner-border spinner-border-sm" />
                  <span style={{fontSize: '14px', fontWeight: 600}}>
                    יוצר מודעה {progress.current} מתוך {progress.total}...
                  </span>
                </div>
                <div style={{
                  height: '6px',
                  background: '#e4e6ef',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${(progress.current / progress.total) * 100}%`,
                    background: progress.errors.length > 0
                      ? 'linear-gradient(90deg, #50cd89, #ffa800)' : '#50cd89',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            ) : (
              <>
                <button onClick={onClose} style={{
                  padding: '10px 24px',
                  border: '1px solid #e4e6ef',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#5e6278',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}>
                  ביטול
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={activePosts.length === 0 || isAnalyzing}
                  style={{
                    padding: '10px 32px',
                    border: 'none',
                    borderRadius: '8px',
                    background: (activePosts.length === 0 || isAnalyzing)
                      ? '#e4e6ef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: (!selectedProfileId || activePosts.length === 0 || isAnalyzing)
                      ? 'not-allowed' : 'pointer',
                  }}
                >
                  🛒 פרסם {activePosts.length} מודעות
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
